/**
 * SearchSelect — комбобокс з пошуком (рішення власника 2026-07-25):
 * «строка подбору як в 1С» замість простині <select> на сотні опцій.
 *
 * Друкуєш у поле — список звужується (пошук за назвою і додатковим
 * текстом, напр. ЄДРПОУ). Стрілки/Enter — вибір з клавіатури, Esc —
 * закрити. Перше застосування — контрагенти у документах; компонент
 * generic (підійде номенклатурі/рахункам).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { signalUserEdit } from '@/store/formDrafts';
import { useAnchoredPopup } from '@/shared/hooks/useAnchoredPopup';

export interface SearchSelectItem {
  id: string;
  label: string;
  /** Додатковий текст для пошуку і сірого підпису (ЄДРПОУ, код). */
  hint?: string | null;
}

interface Props {
  items: SearchSelectItem[];
  value: string;                       // id обраного ('' — не обрано)
  onChange: (id: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Скільки рядків показувати максимум (далі — «уточніть пошук»). */
  maxVisible?: number;
  /** ч.817: стиль поля вводу (напр. рамка «бракує») — без зміни висоти. */
  inputStyle?: React.CSSProperties;
  /** ч.817: мінімальна ширина випадашки (у вузькій колонці — ширша за поле). */
  dropdownMinWidth?: number;
}

export default function SearchSelect({
  items, value, onChange, placeholder = '— почніть вводити назву —',
  disabled = false, maxVisible = 50, inputStyle, dropdownMinWidth,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hi, setHi] = useState(0);     // highlighted index
  const rootRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(
    () => items.find((i) => i.id === value) ?? null,
    [items, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      i.label.toLowerCase().includes(q)
      || (i.hint ?? '').toLowerCase().includes(q));
  }, [items, query]);
  const visible = filtered.slice(0, maxVisible);

  // ч.1327: список — у порталі (таблична частина прокручується вбік і обрізала б його);
  // клік поза полем і поза списком — закрити.
  const close = useCallback(() => { setOpen(false); setQuery(''); }, []);
  const pop = useAnchoredPopup(open && !disabled, rootRef, popRef, close);

  useEffect(() => { setHi(0); }, [query, open]);

  function pick(id: string) {
    // ч.1059: вибір мишею = правка користувача (для чернетки картки/документа).
    signalUserEdit(rootRef.current);
    onChange(id);
    setOpen(false);
    setQuery('');
  }

  return (
    <div ref={rootRef} className="search-select" style={{ position: 'relative' }}>
      <input
        value={open ? query : (selected?.label ?? '')}
        placeholder={selected ? selected.label : placeholder}
        disabled={disabled}
        onFocus={() => { if (!disabled) { setOpen(true); setQuery(''); } }}
        onChange={(e) => { setOpen(true); setQuery(e.target.value); }}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, visible.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
          else if (e.key === 'Enter') {
            e.preventDefault();
            if (visible[hi]) pick(visible[hi].id);
          } else if (e.key === 'Escape') { close(); }
        }}
        style={{ width: '100%', paddingRight: value && !disabled ? 26 : undefined, ...inputStyle }}
        autoComplete="off"
      />
      {/* ч.842 (запит власника): хрестик очищення у правому куті поля. */}
      {value && !disabled && (
        <button type="button" tabIndex={-1} title="Очистити"
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); pick(''); }}
          style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                   border: 'none', background: 'transparent', padding: '0 4px', lineHeight: 1,
                   color: 'var(--muted)', cursor: 'pointer', fontSize: '1rem' }}>×</button>
      )}
      {open && !disabled && createPortal(
        <div ref={popRef} className="search-select__pop" style={{
          ...pop.style,
          // Як раніше: за шириною поля; з dropdownMinWidth — за вмістом, не вужче за поле.
          width: dropdownMinWidth ? 'max-content' : pop.anchorWidth,
          minWidth: dropdownMinWidth ? Math.max(dropdownMinWidth, pop.anchorWidth) : undefined,
          maxWidth: dropdownMinWidth ? 'min(70vw, 640px)' : undefined,
          maxHeight: 320, overflowY: 'auto', textAlign: 'left',
          background: 'var(--bg-panel)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-lg)',
          color: 'var(--text)', fontSize: '0.9rem',
        }}>
          {value && (
            <div
              onMouseDown={(e) => { e.preventDefault(); pick(''); }}
              style={{ padding: '0.3rem 0.6rem', cursor: 'pointer',
                       color: 'var(--muted)', borderBottom: '1px solid var(--border-soft)' }}>
              — очистити —
            </div>
          )}
          {visible.length === 0 && (
            <div style={{ padding: '0.4rem 0.6rem', color: 'var(--muted)' }}>
              Нічого не знайдено.
            </div>
          )}
          {visible.map((i, idx) => (
            <div
              key={i.id}
              onMouseDown={(e) => { e.preventDefault(); pick(i.id); }}
              onMouseEnter={() => setHi(idx)}
              style={{
                padding: '0.3rem 0.6rem', cursor: 'pointer', fontSize: '0.9rem',
                background: idx === hi ? 'var(--bg-soft)' : 'transparent',
                fontWeight: i.id === value ? 600 : 400,
              }}
            >
              {i.label}
              {i.hint && (
                <span style={{ color: 'var(--muted)', marginLeft: 6, fontSize: '0.8rem' }}>
                  {i.hint}
                </span>
              )}
            </div>
          ))}
          {filtered.length > maxVisible && (
            <div style={{ padding: '0.3rem 0.6rem', color: 'var(--muted)', fontSize: '0.8rem' }}>
              …ще {filtered.length - maxVisible} — уточніть пошук
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
